import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { toast } from 'sonner';
import { ShieldAlert, Users, Zap, Ticket, CheckCircle2, ArrowLeft, Crown, School, X, Trash2, KeyRound, Mail, Info } from 'lucide-react';

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_users: 0, pro_users: 0, total_generations: 0, open_tickets: 0, total_institutions: 0 });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalData, setModalData] = useState({ isOpen: false, title: '', items: [], type: '' });

  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradePlan, setUpgradePlan] = useState('ultra');

  const [instName, setInstName] = useState('');
  const [instSlug, setInstSlug] = useState('');
  const [instSeats, setInstSeats] = useState(50); 
  const [instPlan, setInstPlan] = useState('institution_gold');
  // FIX: Preparado para múltiples correos
  const [adminEmail, setAdminEmail] = useState(''); 

  useEffect(() => {
    if (user?.role !== 'admin') return;
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/tickets')
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
    } catch (err) {
      toast.error('Error cargando datos del panel');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailData = async (type) => {
    try {
      const { data } = await api.get(`/admin/${type}`);
      let title = '';
      if (type === 'users') title = 'Directorio de Usuarios';
      if (type === 'generations') title = 'Últimas Clases Generadas';
      if (type === 'institutions') title = 'Colegios Registrados (B2B)';
      
      setModalData({ isOpen: true, title, items: data, type });
    } catch (err) {
      toast.error("Error cargando los detalles");
    }
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();
    if (!upgradeEmail) return toast.error('Ingresá un correo');
    try {
      const { data } = await api.post('/admin/upgrade-user', { email: upgradeEmail, plan: upgradePlan });
      toast.success(data.message);
      setUpgradeEmail('');
      fetchData(); 
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al mejorar usuario');
    }
  };

  const handleResolve = async (ticketId) => {
    try {
      await api.patch(`/admin/tickets/${ticketId}/resolve`);
      toast.success('¡Ticket resuelto!');
      setTickets(tickets.filter(t => t.id !== ticketId));
      setStats(prev => ({ ...prev, open_tickets: prev.open_tickets - 1 }));
    } catch (err) {
      toast.error('Error al resolver ticket');
    }
  };

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    if (!instName || !instSlug || !adminEmail) {
      toast.error('Por favor completá el Nombre, la URL y los Emails de los Directivos.');
      return;
    }
    
    try {
      const { data } = await api.post('/admin/institutions', {
        name: instName,
        slug: instSlug,
        max_seats: parseInt(instSeats),
        plan_to_assign: instPlan,
        admin_email: adminEmail 
      });
      toast.success(data.message, { duration: 5000 });
      setInstName(''); 
      setInstSlug(''); 
      setAdminEmail('');
      setInstSeats(50);
      fetchData(); 
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el colegio.');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`⚠️ ADVERTENCIA: Vas a eliminar PERMANENTEMENTE a ${email} y todas sus dinámicas. ¿Estás seguro?`)) {
      return;
    }
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      toast.success(data.message);
      setModalData(prev => ({
        ...prev,
        items: prev.items.filter(u => u.id !== userId)
      }));
      fetchData(); 
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleResetPassword = async (userId, email) => {
    if (!window.confirm(`¿Resetear la contraseña de ${email} a "DinamiClass123!" ?`)) {
      return;
    }
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, {
        new_password: "DinamiClass123!"
      });
      toast.success(data.message, { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al resetear contraseña');
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-slate-700 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-300 pb-20 relative">
      
      {/* Modal Flotante */}
      {modalData.isOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl animate-fade-in-up">
            <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-950/50 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {modalData.type === 'users' ? <Users className="text-blue-400" size={20}/> 
                : modalData.type === 'institutions' ? <School className="text-emerald-400" size={20}/> 
                : <Zap className="text-violet-400" size={20}/>}
                {modalData.title}
              </h2>
              <button onClick={() => setModalData({ ...modalData, isOpen: false })} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto custom-scrollbar flex-1 space-y-3">
              {modalData.items.length === 0 ? (
                <p className="text-center text-slate-500 mt-10">No hay datos para mostrar.</p>
              ) : (
                modalData.items.map((item, i) => (
                  <div key={i} className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex justify-between items-center hover:border-slate-700 transition-colors">
                    
                    {modalData.type === 'institutions' && (
                      <div className="flex-1 flex justify-between items-start">
                        <div>
                          <p className="text-emerald-400 font-bold text-sm flex items-center gap-2">
                            {item.name} <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded uppercase">{item.slug}</span>
                          </p>
                          <div className="mt-2">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Directivos Asignados:</p>
                            {/* FIX: Ahora renderiza la lista de múltiples directivos de forma elegante */}
                            <div className="flex flex-wrap gap-1">
                                {item.admin_email.split(',').map((email, idx) => (
                                    <span key={idx} className="text-xs bg-slate-900 text-slate-300 border border-slate-800 px-2 py-1 rounded">
                                        {email.trim()}
                                    </span>
                                ))}
                            </div>
                            <p className="text-[9px] text-slate-500 mt-1 italic flex items-center gap-1">
                                <Info size={10} /> Para resetear o eliminar a un director, buscalo en "Directorio de Usuarios".
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xl font-black text-white">{item.used_seats} <span className="text-sm text-slate-500">/ {item.max_seats}</span></p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest">{item.plan_to_assign.replace('_', ' ')}</p>
                        </div>
                      </div>
                    )}

                    {modalData.type === 'users' && (
                      <>
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm flex items-center gap-2">
                            {item.email} {item.role === 'admin' && <Crown size={12} className="text-emerald-400" />}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{item.name} • Registrado: {new Date(item.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
                            item.plan === 'free' ? 'bg-slate-800 text-slate-400' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          }`}>
                            {item.plan.replace('_', ' ')}
                          </span>
                          {item.id !== user.id && (
                            <div className="flex items-center gap-1 border-l border-slate-800 pl-3">
                              <button onClick={() => handleResetPassword(item.id, item.email)} className="p-1.5 text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg"><KeyRound size={16} /></button>
                              <button onClick={() => handleDeleteUser(item.id, item.email)} className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={16} /></button>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {modalData.type === 'generations' && (
                      <>
                        <div>
                          <p className="text-white font-medium text-sm">{item.topic}</p>
                          <p className="text-xs text-slate-500 mt-1">Edad: {item.age} años • Creado: {new Date(item.created_at).toLocaleDateString()}</p>
                        </div>
                        {item.rating && (
                          <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${
                            item.rating === 'up' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                          }`}>
                            {item.rating === 'up' ? 'Útil' : 'A Mejorar'}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cabecera del CEO */}
      <header className="border-b border-slate-800 bg-slate-950 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <ShieldAlert className="text-emerald-500" size={20} />
              <h1 className="text-white font-bold tracking-widest uppercase text-sm">Panel de Control CEO</h1>
            </div>
          </div>
          <span className="text-xs bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full font-bold border border-emerald-500/20">
            Nivel 5: Autorizado
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Estadísticas Vitales */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div onClick={() => fetchDetailData('users')} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:border-blue-500/50 transition-all group">
            <div>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 group-hover:text-blue-400">Total Usuarios</p>
              <p className="text-2xl font-bold text-blue-400">{stats.total_users}</p>
            </div>
          </div>

          <div onClick={() => fetchDetailData('users')} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:border-amber-500/50 transition-all group">
            <div>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 group-hover:text-amber-400">Pro+</p>
              <p className="text-2xl font-bold text-amber-400">{stats.pro_users}</p>
            </div>
          </div>

          <div onClick={() => fetchDetailData('institutions')} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:border-emerald-500/50 transition-all group">
            <div>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 group-hover:text-emerald-400">Colegios B2B</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.total_institutions || 0}</p>
            </div>
          </div>

          {/* ACÁ ELIMINÉ LA PALABRA IA Y LE PUSE "DINÁMICAS" */}
          <div onClick={() => fetchDetailData('generations')} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:border-violet-500/50 transition-all group">
            <div>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1 group-hover:text-violet-400">Dinámicas</p>
              <p className="text-2xl font-bold text-violet-400">{stats.total_generations}</p>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">Tickets</p>
              <p className="text-2xl font-bold text-red-400">{stats.open_tickets}</p>
            </div>
          </div>
        </div>

        {/* Fila Superior: MODO DIOS y VENTA B2B (Lado a Lado) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* MODO DIOS */}
          <div className="bg-gradient-to-b from-slate-900 to-slate-950 border border-emerald-500/30 rounded-2xl p-6 relative overflow-hidden shadow-2xl shadow-emerald-900/10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl" />
            <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2"><Crown size={18} className="text-emerald-400" /> Modo Dios</h2>
            <p className="text-slate-400 text-xs mb-6">Ascendé cuentas o recargá créditos manualmente.</p>

            <form onSubmit={handleUpgrade} className="space-y-4 relative z-10">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Correo del Usuario</label>
                <input type="email" required placeholder="amigo@gmail.com" value={upgradeEmail} onChange={(e) => setUpgradeEmail(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-emerald-500 transition-all" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Seleccionar Plan</label>
                <select value={upgradePlan} onChange={(e) => setUpgradePlan(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-emerald-500 transition-all cursor-pointer">
                  <option value="ultra">Ultra (Infinito)</option>
                  <option value="gold">Gold (120 cr)</option>
                  <option value="pro">Pro (60 cr)</option>
                  <option value="standard">Standard (30 cr)</option>
                </select>
              </div>
              <button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all mt-2 text-sm shadow-lg shadow-emerald-900/20">
                Activar Plan
              </button>
            </form>
          </div>

          {/* VENTA B2B */}
          <div className="bg-gradient-to-b from-slate-900 to-slate-950 border border-blue-500/30 rounded-2xl p-6 relative overflow-hidden shadow-2xl shadow-blue-900/10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl" />
            <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2"><School size={18} className="text-blue-400" /> Venta B2B</h2>
            <p className="text-slate-400 text-xs mb-6">Creá un colegio y asignale directores.</p>

            <form onSubmit={handleCreateInstitution} className="space-y-4 relative z-10">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Nombre</label>
                  <input type="text" required placeholder="Ej: San Martín" value={instName} onChange={e => setInstName(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">URL Slug</label>
                  <input type="text" required placeholder="san-martin" value={instSlug} onChange={e => setInstSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all font-mono" />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider flex items-center justify-between">
                    <span className="flex items-center gap-1"><Mail size={12} /> Emails Directivos (Separar con coma)</span>
                </label>
                {/* FIX: Cambiamos type="email" a type="text" para permitir comas */}
                <input type="text" required placeholder="dir@escuela.com, vice@escuela.com..." value={adminEmail} onChange={e => setAdminEmail(e.target.value)} className="w-full bg-slate-950 border border-emerald-500/50 rounded-xl p-3 text-sm text-white outline-none focus:border-emerald-500 transition-all" />
              </div>

              <div className="flex gap-3">
                <div className="space-y-1.5 flex-1">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Licencia</label>
                  <select value={instPlan} onChange={e => setInstPlan(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all cursor-pointer">
                    <option value="institution_gold">Gold</option>
                    <option value="institution_ultra">Ultra</option>
                  </select>
                </div>
                <div className="space-y-1.5 w-1/3 shrink-0">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Cupos</label>
                  <select value={instSeats} onChange={e => setInstSeats(Number(e.target.value))} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-white outline-none focus:border-blue-500 transition-all cursor-pointer">
                    <option value={50}>Pequeña (50)</option>
                    <option value={175}>Mediana (175)</option>
                    <option value={350}>Grande (350)</option>
                  </select>
                </div>
              </div>
              
              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all mt-2 text-sm shadow-lg shadow-blue-900/20">
                Crear Colegio
              </button>
            </form>
          </div>
        </div>

        {/* Fila Inferior: BANDEJA DE TICKETS (Más ancha, menos alta) */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col h-[400px]">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Ticket size={18} className="text-blue-400" /> Bandeja de Soporte</h2>
          <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
            {tickets.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500">
                <CheckCircle2 size={48} className="mb-3 opacity-20" />
                <p className="text-sm font-medium">Bandeja limpia. Cero problemas.</p>
              </div>
            ) : (
              tickets.map((t) => (
                <div key={t.id} className="bg-slate-950 border border-slate-800 rounded-xl p-4 flex gap-4 transition-all hover:border-slate-700">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded uppercase tracking-wider border border-blue-500/20">{t.reason}</span>
                      <span className="text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-white text-sm font-medium">{t.user_email}</p>
                    <p className="text-slate-500 text-xs mb-2">Contacto alt: {t.contact_info}</p>
                    <div className="bg-slate-900 p-3 rounded-lg text-sm text-slate-300 whitespace-pre-line border border-slate-800">{t.message}</div>
                  </div>
                  <button onClick={() => handleResolve(t.id)} className="p-2 h-10 bg-slate-900 hover:bg-emerald-500/20 text-slate-500 hover:text-emerald-500 rounded-xl border border-slate-800 transition-all">
                    <CheckCircle2 size={20} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </main>
    </div>
  );
}
